import type { AgentBuilderChartDataPoint, AgentBuilderReportData, DashboardTrendGranularity } from './types'
import {
  classifyColumns,
  formatChartDateKey,
  formatFieldDisplayName,
  parseDashboardDate,
  resolveColumnFormat,
} from './vizUtils'
import { CHART_LINE_COLOR, getSeriesColor } from './chartTheme'
import { findDrilldownColumn } from './drilldownHierarchy'
import type { DrilldownPathFilter } from './drilldownHierarchy'

// Pure (non-React) data shaping shared by the on-screen charts and tables (Viz.tsx) and the PDF export.

export const KPI_ACCENT_COLORS = ['#00c8b9', '#2a9d8f', '#4a7fc1', '#7a8fb8', '#c0563f']
export const TABLE_BAR_COLORS = ['#00c7b7', '#2a9d8f', '#4a7fc1', '#7b8fbd', '#8fb3a9']
export const TABLE_NEGATIVE_BAR_COLOR = '#d64545'

export type ExpandedTableResult = {
  rows: Record<string, unknown>[]
  columns: string[]
  dimension: string
  path: DrilldownPathFilter[]
}

/** Card-local presentation state a widget card reports to the viewer, so the PDF can print what is on screen. */
export type WidgetPresentationState = {
  trendGranularity?: DashboardTrendGranularity
  /** Expanded drilldown rows, keyed by buildExpansionRowKey. */
  expandedRows?: Record<string, ExpandedTableResult>
}

function parseChartDateSortValue(value: string | number | null | undefined) {
  return parseDashboardDate(value)?.getTime() ?? null
}

function sortLineChartDataByDate(chartData: AgentBuilderChartDataPoint[]) {
  const sortable = chartData.map((point, index) => ({
    point,
    index,
    sortValue: parseChartDateSortValue(point.label),
  }))
  const datedPoints = sortable.filter((item) => item.sortValue !== null)

  if (datedPoints.length < 2 || datedPoints.length !== sortable.length) {
    return chartData
  }

  return [...sortable]
    .sort((left, right) => {
      const dateDelta = (left.sortValue ?? 0) - (right.sortValue ?? 0)
      return dateDelta === 0 ? left.index - right.index : dateDelta
    })
    .map((item) => item.point)
}

function formatBucketDate(year: number, month = 0) {
  return `${String(month + 1).padStart(2, '0')}/01/${year}`
}

export function aggregateLineChartDataByDate(chartData: AgentBuilderChartDataPoint[], granularity: DashboardTrendGranularity) {
  const sortedChartData = sortLineChartDataByDate(chartData)
  if (granularity === 'daily') {
    return sortedChartData.map((point) => ({
      ...point,
      label: formatChartDateKey(point.label),
    }))
  }

  const buckets = new Map<string, {
    label: string
    sortValue: number
    value: number
    series: Map<string, number>
  }>()
  let hasParsedDate = false
  let hasDateBucket = false

  sortedChartData.forEach((point) => {
    const date = parseDashboardDate(point.label)
    if (!date) return
    hasParsedDate = true

    hasDateBucket = true
    const bucketYear = date.getFullYear()
    const bucketMonth = granularity === 'yearly' ? 0 : date.getMonth()
    const key = granularity === 'yearly'
      ? String(bucketYear)
      : `${bucketYear}-${String(bucketMonth + 1).padStart(2, '0')}`
    const label = granularity === 'yearly'
      ? formatBucketDate(bucketYear)
      : formatBucketDate(bucketYear, bucketMonth)
    const sortValue = new Date(bucketYear, bucketMonth, 1).getTime()
    const bucket = buckets.get(key) ?? { label, sortValue, value: 0, series: new Map<string, number>() }

    bucket.value += point.value
    point.series?.forEach((seriesPoint) => {
      bucket.series.set(seriesPoint.name, (bucket.series.get(seriesPoint.name) ?? 0) + seriesPoint.value)
    })
    buckets.set(key, bucket)
  })

  if (!hasDateBucket) return hasParsedDate ? [] : sortedChartData

  return Array.from(buckets.values())
    .sort((left, right) => left.sortValue - right.sortValue)
    .map((bucket) => ({
      label: bucket.label,
      value: bucket.value,
      series: Array.from(bucket.series.entries()).map(([name, value]) => ({ name, value })),
    }))
}

export function shiftChartPointLabelsByYears(chartData: AgentBuilderChartDataPoint[], deltaYears: number) {
  return chartData.map((point) => {
    const date = parseDashboardDate(point.label)
    if (!date) return point
    const shifted = new Date(date.getFullYear() + deltaYears, date.getMonth(), date.getDate())
    if (shifted.getMonth() !== date.getMonth()) {
      shifted.setDate(0)
    }
    return {
      ...point,
      label: formatChartDateKey(`${shifted.getFullYear()}-${String(shifted.getMonth() + 1).padStart(2, '0')}-${String(shifted.getDate()).padStart(2, '0')}`),
    }
  })
}

function getLinePointSeries(point: AgentBuilderChartDataPoint, fallbackName: string) {
  return point.series?.length
    ? point.series
    : [{ name: fallbackName, value: point.value }]
}

export function isPreviousYearComparisonSeries(name: string) {
  const normalized = String(name ?? '')
    .trim()
    .toLowerCase()
    .replace(/['"[\]()]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

  return /\b(ly|last year|previous year|prior year|py)\b/.test(normalized)
}

function getSingleLineMeasureSeries(
  point: AgentBuilderChartDataPoint,
  fallbackName: string,
  preferCurrentPeriodMeasure: boolean,
) {
  const series = getLinePointSeries(point, fallbackName)
  const currentPeriodSeries = preferCurrentPeriodMeasure
    ? series.filter((seriesPoint) => !isPreviousYearComparisonSeries(seriesPoint.name))
    : series

  return (currentPeriodSeries.length > 0 ? currentPeriodSeries : series).slice(0, 1)
}

export function buildLineChartRows(
  currentPoints: AgentBuilderChartDataPoint[],
  comparisonPoints: AgentBuilderChartDataPoint[],
  fallbackMeasure: string,
  omitCurrentPreviousYearSeries: boolean,
) {
  const rowsByLabel = new Map<string, Record<string, string | number>>()
  const seriesNames = new Set<string>()

  currentPoints.forEach((point) => {
    const label = String(point.label ?? '')
    const row = rowsByLabel.get(label) ?? { label }
    getSingleLineMeasureSeries(point, fallbackMeasure, omitCurrentPreviousYearSeries).forEach((seriesPoint) => {
      const key = `current:${seriesPoint.name}`
      seriesNames.add(key)
      row[key] = seriesPoint.value
    })
    rowsByLabel.set(label, row)
  })

  comparisonPoints.forEach((point) => {
    const label = String(point.label ?? '')
    const row = rowsByLabel.get(label) ?? { label }
    getSingleLineMeasureSeries(point, fallbackMeasure, omitCurrentPreviousYearSeries).forEach((seriesPoint) => {
      const key = `previous:${seriesPoint.name}`
      seriesNames.add(key)
      row[key] = seriesPoint.value
    })
    rowsByLabel.set(label, row)
  })

  const rows = Array.from(rowsByLabel.values()).sort((left, right) => {
    const leftSort = parseChartDateSortValue(String(left['label'] ?? '')) ?? 0
    const rightSort = parseChartDateSortValue(String(right['label'] ?? '')) ?? 0
    return leftSort - rightSort
  })

  return { rows, seriesKeys: Array.from(seriesNames) }
}

/** The returned column behind a `current:` / `previous:` line series key. */
export function lineSeriesColumn(key: string) {
  return key.split(':').slice(1).join(':') || key
}

export function formatLineSeriesName(key: string) {
  const label = formatFieldDisplayName(lineSeriesColumn(key))
  return key.startsWith('previous:') ? `${label} PY` : label
}

export function getLineSeriesColor(seriesKey: string, seriesKeys: string[]) {
  const rawName = lineSeriesColumn(seriesKey)
  const measureNames = Array.from(new Set(seriesKeys.map(lineSeriesColumn)))
  const measureIndex = Math.max(0, measureNames.indexOf(rawName))
  return measureIndex === 0 ? CHART_LINE_COLOR : getSeriesColor(measureIndex)
}

export function resolveBackendPagination(data: AgentBuilderReportData) {
  if (data.hasPaginationMetadata === true && data.totalRows != null && data.page != null && data.pageSize != null) {
    return { totalRows: data.totalRows, page: data.page, pageSize: data.pageSize }
  }
  return null
}

export function tableBarColor(index: number): string {
  const count = TABLE_BAR_COLORS.length
  return TABLE_BAR_COLORS[((index % count) + count) % count] ?? '#00c7b7'
}

/** Numeric measure columns, decided per column from the whole dataset. Columns with a text format (IDs) stay left-aligned. */
export function resolveNumericColumns(data: AgentBuilderReportData): Set<string> {
  const { measureCols } = classifyColumns(data.columns, data.rows)
  return new Set(measureCols.filter((column) => resolveColumnFormat(data.columnFormats, column)?.kind !== 'text'))
}

export function isEmptyCellValue(value: unknown): boolean {
  return value === null || value === undefined || value === '' || (typeof value === 'number' && Number.isNaN(value))
}

/** Fill width (percent of the cell) for an in-cell bar: 0 for a zero value, otherwise at least 1.5% and at most 100%. */
export function inCellBarPercent(value: number, max: number): number {
  return value === 0 || max <= 0 ? 0 : Math.min(100, Math.max(1.5, (Math.abs(value) / max) * 100))
}

/** Max |value| per column, computed once over the given top-level rows. */
export function buildColumnBarMax(
  columns: string[],
  rows: Record<string, unknown>[],
  valueOf: (row: Record<string, unknown>, column: string, columnIndex: number) => unknown,
): Record<string, number> {
  const max: Record<string, number> = {}
  columns.forEach((column, columnIndex) => {
    let columnMax = 0
    rows.forEach((row) => {
      const value = valueOf(row, column, columnIndex)
      if (typeof value === 'number' && Number.isFinite(value)) columnMax = Math.max(columnMax, Math.abs(value))
    })
    max[column] = columnMax || 1
  })
  return max
}

/** Cell resolution for a row inside an expandable (drilldown) table. */
export function resolveExpandableCellValue(row: Record<string, unknown>, column: string, columnIndex: number, columns: string[]): unknown {
  // Cell resolution for a drilldown child row (depth > 0):
  //  1. Same-named key on the child row.
  //  2. A child column whose normalized name matches the parent's.
  //  3. Positional fallback — the child's Nth column fills the
  //     parent's Nth slot. Needed because drilldown can now jump
  //     to a deeper level (e.g. ClientGroup → JobId), so the
  //     child's dimension column has no name analog in the
  //     parent's header and would otherwise render as "-".
  // Treat `null` the same as `undefined` for fallback purposes: some backends emit
  // explicit `null` placeholders for parent-header keys while the real value lives
  // under a drilldown alias or positional column.
  let value: unknown = row[column]
  if (value === undefined || value === null) {
    const sourceColumn = findDrilldownColumn(columns, column)
    if (sourceColumn) {
      const aliasValue = row[sourceColumn]
      if (aliasValue !== undefined && aliasValue !== null) value = aliasValue
    }
  }
  if (value === undefined || value === null) {
    const positional = columns[columnIndex]
    if (positional) {
      const positionalValue = row[positional]
      if (positionalValue !== undefined && positionalValue !== null) value = positionalValue
    }
  }
  return value
}

/** Key of an expanded drilldown row: the parent's key, then this row's values for `columns`. */
export function buildExpansionRowKey(row: Record<string, unknown>, columns: string[], parentKey = '') {
  return `${parentKey}/${columns.map((column) => String(row[column] ?? '')).join('|')}`
}

export type PivotMatrix = {
  rowKeys: string[]
  columnKeys: string[]
  matrix: Map<string, Map<string, number>>
}

/**
 * Pivot long rows into a row × column matrix of summed numeric values. Keys keep first-appearance
 * order; a missing dimension value becomes '—'. Non-numeric measure values stay out of the matrix,
 * so their cell renders as an empty dash.
 */
export function buildPivotMatrix(
  rows: Record<string, unknown>[],
  rowField: string,
  columnField: string,
  valueField: string,
): PivotMatrix {
  const rowKeys = Array.from(new Set(rows.map((row) => String(row[rowField] ?? '—'))))
  const columnKeys = Array.from(new Set(rows.map((row) => String(row[columnField] ?? '—'))))
  const matrix = new Map<string, Map<string, number>>()
  rows.forEach((row) => {
    const raw = row[valueField]
    if (typeof raw !== 'number') return
    const rowKey = String(row[rowField] ?? '—')
    const columnKey = String(row[columnField] ?? '—')
    const rowMap = matrix.get(rowKey) ?? new Map<string, number>()
    rowMap.set(columnKey, (rowMap.get(columnKey) ?? 0) + raw)
    matrix.set(rowKey, rowMap)
  })
  return { rowKeys, columnKeys, matrix }
}

/** Sum of a pivot row across the given column keys (missing cells count as 0). */
export function pivotRowTotal(matrix: PivotMatrix['matrix'], rowKey: string, columnKeys: string[]) {
  const rowMap = matrix.get(rowKey)
  return columnKeys.reduce((sum, columnKey) => sum + (rowMap?.get(columnKey) ?? 0), 0)
}
